import { z } from 'zod';
/** ISO 4217 currency code, e.g. "EUR". */
export const CurrencySchema = z.string().regex(/^[A-Z]{3}$/);
/**
 * Money is always an integer number of minor units (cents) plus a currency.
 * Never use floating point for prices.
 */
export const MoneySchema = z.object({
    amountMinor: z.number().int().nonnegative(),
    currency: CurrencySchema,
});
/**
 * Kiosk languages (owner, 14.10.2026): Portugal's main expat communities.
 * Catalogue texts may exist only in PT/EN; other languages fall back to English, then Portuguese.
 */
export const LOCALES = ['pt', 'en', 'fr', 'es', 'ru', 'uk', 'de'];
export const LocaleSchema = z.enum(LOCALES);
/** Native names (a language menu is read by people who do not know the current language). */
export const LOCALE_NAMES = {
    pt: 'Português',
    en: 'English',
    fr: 'Français',
    es: 'Español',
    ru: 'Русский',
    uk: 'Українська',
    de: 'Deutsch',
};
/** BCP 47 tags for dates and money. */
export const LOCALE_TAGS = {
    pt: 'pt-PT',
    en: 'en-GB',
    fr: 'fr-FR',
    es: 'es-ES',
    ru: 'ru-RU',
    uk: 'uk-UA',
    de: 'de-DE',
};
/** Text in every UI language. `pt` is mandatory; missing languages fall back to `en`, then `pt`. */
export const LocalizedTextSchema = z.object({
    pt: z.string(),
    en: z.string().optional(),
    fr: z.string().optional(),
    es: z.string().optional(),
    ru: z.string().optional(),
    uk: z.string().optional(),
    de: z.string().optional(),
});
export function localize(text, locale) {
    return text[locale] ?? text.en ?? text.pt;
}
export const ServiceKindSchema = z.enum(['face', 'body', 'skin', 'combo', 'addon']);
/**
 * A bookable service as shown by the kiosk. `externalId` is the CRM's id
 * (Altegio service id); `id` is stable inside Hub.
 */
export const ServiceSchema = z.object({
    id: z.string(),
    externalId: z.string(),
    locationId: z.string(),
    kind: ServiceKindSchema,
    title: LocalizedTextSchema,
    description: LocalizedTextSchema.optional(),
    durationMin: z.number().int().positive(),
    price: MoneySchema,
    /** Price source decides which system wins when CRM and website disagree. */
    priceSource: z.enum(['website', 'crm']),
    imageUrl: z.string().url().optional(),
    /** Shown only to customers without previous visits (e.g. "First Express Facial"). */
    firstVisitOnly: z.boolean().default(false),
    /** Staff (Hub ids) who perform this service. Empty = any bookable staff. */
    staffIds: z.array(z.string()).default([]),
    active: z.boolean().default(true),
});
export const StaffSchema = z.object({
    id: z.string(),
    externalId: z.string(),
    locationId: z.string(),
    displayName: z.string(),
    /** Whether customers may book this person online / at the kiosk. */
    bookable: z.boolean(),
    /**
     * Test therapist (owner 21.10.2026, "Mila · TESTE Hub"): offered only on a kiosk opened with
     * `?test=1`; the only one whose bookings are written to the CRM while writes are in test mode.
     */
    test: z.boolean().optional(),
    /**
     * Hub-relative URL of the therapist's photo (served by the API, e.g.
     * `/v1/locations/parede/staff/olena/photo`). The CRM's own image URL never reaches the browser.
     */
    photoUrl: z.string().optional(),
    /** Short role/specialization from the CRM (e.g. "TOP Face & body master"). */
    title: z.string().max(80).optional(),
    /**
     * Client rating from the CRM's online booking (Altegio `book_staff`: rating, votes_count), only
     * when the studio shows ratings there (`show_rating`). Kiosk v3 shows it under the photo.
     */
    rating: z.number().min(0).max(5).optional(),
    /** How many clients rated (Altegio `votes_count`). */
    ratingCount: z.number().int().min(0).optional(),
});
export const SlotSchema = z.object({
    staffId: z.string(),
    /** Local wall-clock start with offset, ISO 8601 (e.g. 2026-10-21T10:30:00+01:00). */
    start: z.string(),
    durationMin: z.number().int().positive(),
});
export function formatMoney(m, locale) {
    return new Intl.NumberFormat(LOCALE_TAGS[locale], {
        style: 'currency',
        currency: m.currency,
        currencyDisplay: 'narrowSymbol',
    }).format(m.amountMinor / 100);
}
/**
 * A retail product for home care, sold at the location. Stock and price come from the CRM
 * (Altegio "goods", read-only); photo and texts can be overlaid by Hub.
 */
export const ProductSchema = z.object({
    /** Hub id: `p-<CRM id>`. */
    id: z.string().regex(/^p-[a-z0-9-]{1,37}$/),
    externalId: z.string(),
    locationId: z.string(),
    title: LocalizedTextSchema,
    description: LocalizedTextSchema.optional(),
    price: MoneySchema,
    imageUrl: z.string().url().optional(),
    /** Units in stock when the CRM reports it; products with 0 are never offered. */
    inStock: z.number().int().optional(),
});
/** Integer-only discount, rounded down to whole cents in the customer's favour. */
export function applyPercentOff(m, percentOff) {
    const pct = Math.min(Math.max(Math.trunc(percentOff), 0), 90);
    return { amountMinor: Math.floor((m.amountMinor * (100 - pct)) / 100), currency: m.currency };
}
//# sourceMappingURL=catalog.js.map